import type {
  AgentStreamEvent,
  ApiErrorShape,
  AutomationActivity,
  AutomationRun,
  Job,
  JobEvent,
  KnowledgeStageStatus,
  Project,
  ProjectCreatePayload,
  ProjectCreateResponse,
} from './types'

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string, public requestId?: string | null) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as ApiErrorShape
    throw new ApiError(
      body.error?.message ?? `Request failed with status ${response.status}`,
      response.status,
      body.error?.code,
      body.error?.request_id,
    )
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

/** Split an SSE buffer into whole `event:`/`data:` records, keeping the remainder. */
function parseEventStream(buffer: string): { events: AgentStreamEvent[]; rest: string } {
  const records = buffer.split('\n\n')
  const rest = records.pop() ?? ''
  const events: AgentStreamEvent[] = []
  for (const record of records) {
    let type = ''
    const data: string[] = []
    for (const line of record.split('\n')) {
      if (line.startsWith('event:')) type = line.slice(6).trim()
      else if (line.startsWith('data:')) data.push(line.slice(5).trimStart())
    }
    if (!type || data.length === 0) continue // comments and heartbeats carry no event
    try {
      events.push({ type, ...JSON.parse(data.join('\n')) } as AgentStreamEvent)
    } catch {
      // A truncated record cannot be recovered; skipping it keeps the turn alive.
    }
  }
  return { events, rest }
}

/**
 * Stream one chat turn, calling `onEvent` as progress and answer tokens arrive.
 * Resolves when the stream closes; rejects on a transport or HTTP-level failure.
 */
async function streamChat(
  projectId: string,
  message: string,
  threadId: string | null,
  onEvent: (event: AgentStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`/api/v1/projects/${projectId}/agent/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ message, thread_id: threadId || null }),
    signal,
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as ApiErrorShape
    throw new ApiError(
      body.error?.message ?? `Request failed with status ${response.status}`,
      response.status,
      body.error?.code,
      body.error?.request_id,
    )
  }
  if (!response.body) throw new ApiError('This browser cannot read streamed responses', 0)
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const { events, rest } = parseEventStream(buffer)
      buffer = rest
      events.forEach(onEvent)
    }
  } finally {
    reader.releaseLock()
  }
}

/**
 * Follow a project's automation activity, calling `onActivity` for every line.
 *
 * `EventSource` rather than a streamed fetch: the browser reconnects on its own and
 * resends `Last-Event-ID`, so a dropped connection resumes exactly where it stopped
 * instead of replaying a run from the beginning or silently missing the middle of it.
 * Returns a function that closes the connection.
 */
function followAutomation(
  projectId: string,
  onActivity: (activity: AutomationActivity) => void,
  onStatus?: (connected: boolean) => void,
): () => void {
  const source = new EventSource(`/api/v1/projects/${projectId}/automation/stream`)
  source.addEventListener('open', () => onStatus?.(true))
  source.addEventListener('error', () => onStatus?.(false))
  source.addEventListener('activity', (event) => {
    try {
      onActivity(JSON.parse((event as MessageEvent<string>).data) as AutomationActivity)
    } catch {
      // A truncated record cannot be recovered; skipping it keeps the feed alive.
    }
  })
  return () => source.close()
}

export const api = {
  listProjects: () => request<Project[]>('/api/v1/projects'),
  getProject: (id: string) => request<Project>(`/api/v1/projects/${id}`),
  createProject: (payload: ProjectCreatePayload) => request<ProjectCreateResponse>('/api/v1/projects', { method: 'POST', body: JSON.stringify(payload) }),
  renameProject: (id: string, name: string) => request<Project>(`/api/v1/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  deleteProject: (id: string) => request<void>(`/api/v1/projects/${id}`, { method: 'DELETE' }),
  listJobs: (projectId: string) => request<Job[]>(`/api/v1/projects/${projectId}/jobs`),
  getJob: (jobId: string) => request<Job>(`/api/v1/jobs/${jobId}`),
  listEvents: (jobId: string) => request<JobEvent[]>(`/api/v1/jobs/${jobId}/events?limit=1000`),
  retryJob: (jobId: string) => request<Job>(`/api/v1/jobs/${jobId}/retry`, { method: 'POST' }),
  stopJob: (jobId: string) => request<Job>(`/api/v1/jobs/${jobId}/stop`, { method: 'POST' }),
  resumeJob: (jobId: string) => request<Job>(`/api/v1/jobs/${jobId}/resume`, { method: 'POST' }),
  resetJobToStage: (jobId: string, stage: string) => request<Job>(`/api/v1/jobs/${jobId}/reset`, { method: 'POST', body: JSON.stringify({ stage }) }),
  runKnowledgeBundle: (projectId: string) => request<Job>(`/api/v1/projects/${projectId}/knowledge-bundle/runs`, { method: 'POST' }),
  runKnowledgeStage: (projectId: string, stage: string) => request<Job>(`/api/v1/projects/${projectId}/knowledge-bundle/stages/${stage}/runs`, { method: 'POST' }),
  knowledgeStages: (projectId: string) => request<KnowledgeStageStatus[]>(`/api/v1/projects/${projectId}/knowledge-bundle/stages`),
  listAutomationRuns: (projectId: string) => request<AutomationRun[]>(`/api/v1/projects/${projectId}/automation/runs?limit=50`),
  getAutomationRun: (runId: string) => request<AutomationRun>(`/api/v1/automation/runs/${runId}`),
  triggerAutomation: (projectId: string, trigger: string, resourceId: string) => request<AutomationRun>(`/api/v1/projects/${projectId}/automation/runs`, { method: 'POST', body: JSON.stringify({ trigger, resource_id: resourceId }) }),
  // Stop is cooperative — the run finishes its current step first, so the response
  // reports "stopping" and the feed shows "stopped" a moment later.
  stopAutomationRun: (runId: string) => request<AutomationRun>(`/api/v1/automation/runs/${runId}/stop`, { method: 'POST' }),
  resumeAutomationRun: (runId: string) => request<AutomationRun>(`/api/v1/automation/runs/${runId}/resume`, { method: 'POST' }),
  // Returns the NEW run, not the one that was retried.
  retryAutomationRun: (runId: string) => request<AutomationRun>(`/api/v1/automation/runs/${runId}/retry`, { method: 'POST' }),
  followAutomation,
  // The non-streaming POST /agent/chat still exists server-side, but the UI always
  // streams so it can show progress while a turn runs.
  streamChat,
}

export { parseEventStream }

export const queryKeys = {
  projects: ['projects'] as const,
  project: (id: string) => ['projects', id] as const,
  jobs: (id: string) => ['projects', id, 'jobs'] as const,
  stages: (id: string) => ['projects', id, 'knowledge-stages'] as const,
  events: (id: string) => ['jobs', id, 'events'] as const,
  automationRuns: (id: string) => ['projects', id, 'automation-runs'] as const,
}
